import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import * as classificationApi from '../services/classificationApi';

const ABC_COLORS = { A: 'var(--color-danger)', B: 'var(--color-warning)', C: 'var(--color-text-muted)' };
const XYZ_COLORS = { X: 'var(--color-success)', Y: 'var(--color-warning)', Z: 'var(--color-danger)' };
const FSN_COLORS = { F: 'var(--color-success)', S: 'var(--color-warning)', N: 'var(--color-text-muted)' };

function ClassBadge({ value, colors }) {
  if (!value) return <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>—</span>;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: colors[value] || 'var(--color-text-muted)',
        color: '#fff',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
      }}
    >
      {value}
    </span>
  );
}

export default function ClassificationPage() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ abcClass: '', xyzClass: '', fsnClass: '' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = (f) => {
    const params = {};
    if (f.abcClass) params.abcClass = f.abcClass;
    if (f.xyzClass) params.xyzClass = f.xyzClass;
    if (f.fsnClass) params.fsnClass = f.fsnClass;
    return classificationApi.listClassifications(params).then(setItems);
  };

  useEffect(() => {
    setLoading(true);
    load(filters)
      .catch((err) => setError(err.message || 'Could not load classifications.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await classificationApi.refreshClassifications();
      await load(filters);
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>ABC / XYZ / FSN Classification</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            ABC: Pareto analysis by dispatch volume (no price data exists in this system, so
            volume is the value proxy). XYZ: demand variability (order history). FSN: how
            recently and often a design actually ships.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Classifying…' : 'Refresh classification'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <select style={{ ...inputStyle, width: 'auto' }} value={filters.abcClass} onChange={(e) => setFilters({ ...filters, abcClass: e.target.value })}>
          <option value="">All ABC</option>
          <option value="A">A — top volume</option>
          <option value="B">B — mid volume</option>
          <option value="C">C — low volume</option>
        </select>
        <select style={{ ...inputStyle, width: 'auto' }} value={filters.xyzClass} onChange={(e) => setFilters({ ...filters, xyzClass: e.target.value })}>
          <option value="">All XYZ</option>
          <option value="X">X — predictable</option>
          <option value="Y">Y — moderate</option>
          <option value="Z">Z — unpredictable</option>
        </select>
        <select style={{ ...inputStyle, width: 'auto' }} value={filters.fsnClass} onChange={(e) => setFilters({ ...filters, fsnClass: e.target.value })}>
          <option value="">All FSN</option>
          <option value="F">F — fast-moving</option>
          <option value="S">S — slow-moving</option>
          <option value="N">N — non-moving</option>
        </select>
      </div>

      <Card title="Designs">
        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                {['Design', 'ABC', 'XYZ', 'FSN'].map((h) => (
                  <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.classification_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{i.design_name}</td>
                  <td style={{ padding: '8px 4px' }}><ClassBadge value={i.abc_class} colors={ABC_COLORS} /></td>
                  <td style={{ padding: '8px 4px' }}><ClassBadge value={i.xyz_class} colors={XYZ_COLORS} /></td>
                  <td style={{ padding: '8px 4px' }}><ClassBadge value={i.fsn_class} colors={FSN_COLORS} /></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No classifications match this filter.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}
